import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@feathr/components';
import { Collection } from '@feathr/rachis';

/** The environment profiles the local anhinga backend can be switched between. */
export type TAnhingaEnvProfile = 'development' | 'staging' | 'production';

/** Ordered list of profiles for rendering. */
export const ANHINGA_ENV_PROFILES: TAnhingaEnvProfile[] = ['development', 'staging', 'production'];

/** How long to wait between `/version` polls while anhinga restarts. */
export const POLL_INTERVAL_MS = 10000;

/** Maximum number of `/version` polls before giving up. */
export const MAX_POLL_ATTEMPTS = 20;

/** The subset of the backend version payload needed to detect the current profile. */
interface IAnhingaEnvInfo {
  DB_ENV: string;
  ES_ENV: string;
  FLASK_ENV: string;
}

interface IUseAnhingaEnvSwitcherReturn {
  /** The profile the backend is currently running, or undefined while loading/unknown. */
  currentProfile: TAnhingaEnvProfile | undefined;
  /** Whether a switch is in progress. */
  isSwitching: boolean;
  /** The profile being switched to, or undefined when idle. */
  switchingTo: TAnhingaEnvProfile | undefined;
  /** Rewrites anhinga's .env to the profile, recreates docker, then reloads the page. */
  handleSwitch: (profile: TAnhingaEnvProfile) => Promise<void>;
}

/**
 * Detect which profile the backend is running based on its DB/ES environment.
 *
 * @param version - The backend version payload, or undefined while loading.
 * @returns The matching profile, or undefined when it doesn't match a known profile.
 */
function detectProfile(version: IAnhingaEnvInfo | undefined): TAnhingaEnvProfile | undefined {
  if (!version) {
    return undefined;
  }
  if (version.DB_ENV === 'development' && version.ES_ENV === 'development') {
    return 'development';
  }
  if (version.DB_ENV === 'staging' && version.ES_ENV === 'staging') {
    return 'staging';
  }
  if (version.DB_ENV === 'production' && version.ES_ENV === 'production') {
    return 'production';
  }
  return undefined;
}

/**
 * Fetch the backend version payload, returning undefined if the backend is
 * unreachable (expected while containers are restarting).
 *
 * @returns The version payload, or undefined on any failure.
 */
async function fetchVersion(): Promise<IAnhingaEnvInfo | undefined> {
  try {
    const response = await fetch(`${BLACKBOX_URL}version`, {
      method: 'GET',
      headers: Collection.prototype.getHeaders(),
    });
    if (!response.ok) {
      return undefined;
    }
    return (await response.json()).data as IAnhingaEnvInfo;
  } catch {
    return undefined;
  }
}

/**
 * Resolve once the backend reports the target profile, or reject after the
 * poll budget is exhausted.
 *
 * @param profile - The profile the backend is expected to report.
 * @returns A promise that resolves when the backend is back on the target profile.
 */
function waitForProfile(profile: TAnhingaEnvProfile): Promise<void> {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    async function poll(): Promise<void> {
      attempts += 1;
      if (detectProfile(await fetchVersion()) === profile) {
        resolve();
        return;
      }
      if (attempts >= MAX_POLL_ATTEMPTS) {
        reject(new Error('Timed out waiting for the backend to restart.'));
        return;
      }
      setTimeout(() => {
        poll().catch(reject);
      }, POLL_INTERVAL_MS);
    }

    poll().catch(reject);
  });
}

/**
 * Drives the anhinga environment switcher on the Version page: detects the
 * current profile, posts the switch to the dev-server endpoint, waits for the
 * backend to come back, and reloads so the page reflects the new environment.
 *
 * @param version - The backend version payload from useBlackboxVersion.
 * @returns The current profile, in-flight state, and the switch handler.
 * @example
 * const { currentProfile, isSwitching, switchingTo, handleSwitch } =
 *   useAnhingaEnvSwitcher(blackboxVersion);
 */
function useAnhingaEnvSwitcher(version: IAnhingaEnvInfo | undefined): IUseAnhingaEnvSwitcherReturn {
  const { t } = useTranslation();
  const [switchingTo, setSwitchingTo] = useState<TAnhingaEnvProfile | undefined>();

  async function handleSwitch(profile: TAnhingaEnvProfile): Promise<void> {
    setSwitchingTo(profile);
    try {
      const response = await fetch('/__dev/anhinga-env', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile }),
      });
      let result: { ok?: boolean; error?: string } = {};
      try {
        result = await response.json();
      } catch {
        // A non-JSON response (e.g. a 404 HTML page) means the dev-server
        // endpoint isn't registered — usually the dev server needs a restart.
        result = {};
      }
      if (!response.ok || !result.ok) {
        const detail =
          result.error ??
          (response.status === 404
            ? t('Restart the extender dev server and try again.')
            : t('Request failed ({{status}}).', { status: response.status }));
        toast(t("Couldn't switch environment. {{detail}}", { detail }), { type: 'error' });
        setSwitchingTo(undefined);
        return;
      }
      await waitForProfile(profile);
      globalThis.location.reload();
    } catch {
      toast(t("Couldn't switch environment. Try again."), { type: 'error' });
      setSwitchingTo(undefined);
    }
  }

  return {
    currentProfile: detectProfile(version),
    isSwitching: switchingTo !== undefined,
    switchingTo,
    handleSwitch,
  };
}

export default useAnhingaEnvSwitcher;
